import { Box, Text } from '@user-portal/wikr-components';
import { SidebarEventLabels } from 'analytics/constants/labels';
import { trackSidebarLinkClick } from 'analytics/trackers/sidebar';
import { NAV_ITEMS } from 'navigation/config';
import { resolveNavItems } from 'navigation/resolveNavItems';
import { useNavAccess } from 'navigation/useNavAccess';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { logout } from 'store/auth/actions';

import { useDispatch, useSidebar } from 'hooks';
import { useHandleContactRequest } from 'hooks/common';

import SidebarLink from './components/SidebarLink';
import SidebarLinkSecondary from './components/SidebarLinkSecondary';
import { useGetSecondaryLinks } from './constants';
import { linkType } from './types';

import * as S from './styled';

export default function SideBar() {
    const { t } = useTranslation();

    const { onClick, isLoading } = useHandleContactRequest();

    const sidebarLinks = useGetSecondaryLinks();

    const navAccess = useNavAccess();
    const primaryLinks = useMemo(() => resolveNavItems(NAV_ITEMS, navAccess), [navAccess]);

    const dispatch = useDispatch();

    const { isOpen, close } = useSidebar();

    const navigate = useNavigate();

    const handleLogout = () => {
        trackSidebarLinkClick(SidebarEventLabels.logout);

        setTimeout(() => {
            dispatch(logout());
            navigate('/');
            close();
        }, 300);
    };

    const handleLinkClick = (analyticLabel?: SidebarEventLabels) => () => {
        analyticLabel && trackSidebarLinkClick(analyticLabel);
        close();
    };

    return (
        <S.SideBarWrapper $isOpen={isOpen}>
            <S.SideBarOverlay onClick={close} />
            <S.SideBarMenu>
                <Box $paddingTop={24} $paddingX={12}>
                    {primaryLinks.map(({ path, title, dataLocator, icon, analyticLabel }) => (
                        <SidebarLink
                            icon={icon}
                            key={path}
                            onClick={handleLinkClick(analyticLabel)}
                            to={path}
                            title={t(title)}
                            dataLocator={dataLocator}
                            type={linkType.Primary}
                        />
                    ))}
                    <S.Hr />
                </Box>
                <Box $paddingTop={40} $paddingX={12}>
                    {sidebarLinks.map(({ dataLocator, title, to, analyticLabel, target }) => (
                        <SidebarLinkSecondary
                            key={dataLocator}
                            onClick={handleLinkClick(analyticLabel)}
                            to={to}
                            title={t(title)}
                            dataLocator={dataLocator}
                            target={target}
                        />
                    ))}

                    <S.NavSecondaryButton
                        disabled={isLoading}
                        $isLoading={isLoading}
                        onClick={onClick}
                        data-locator="contactUsLink"
                    >
                        <Text type="small-text">{t('basics.contactUs')}</Text>
                    </S.NavSecondaryButton>

                    <S.NavSecondaryButton onClick={handleLogout} data-locator="burgerLogoutBtn">
                        <Text type="small-text">{t('sidebar.logout')}</Text>
                    </S.NavSecondaryButton>
                </Box>
            </S.SideBarMenu>
        </S.SideBarWrapper>
    );
}
